import type { Locale } from "./locale";

const LOCALE_TO_BCP47: Record<Locale, string> = {
  en: "en-US",
  fr: "fr-FR",
};

const LOCALE_TO_CURRENCY: Record<Locale, string> = {
  en: "USD",
  fr: "EUR",
};

export type CurrencyAffixes = {
  prefix: string;
  suffix: string;
};

function getCurrencyParts(
  locale: Locale,
  currency = LOCALE_TO_CURRENCY[locale],
) {
  return new Intl.NumberFormat(LOCALE_TO_BCP47[locale], {
    style: "currency",
    currency,
    currencyDisplay: "narrowSymbol",
    maximumFractionDigits: 0,
    minimumFractionDigits: 0,
  }).formatToParts(0);
}

export function formatCurrency(
  amount: number,
  locale: Locale,
  options?: { fractionDigits?: number; currency?: string },
): string {
  const fractionDigits = options?.fractionDigits ?? 0;
  return amount.toLocaleString(LOCALE_TO_BCP47[locale], {
    style: "currency",
    // Currency normally follows the UI locale, but callers can override it for
    // edge-case patients whose billing currency differs from their app
    // language (e.g. a French-speaking patient billed in USD).
    currency: options?.currency ?? LOCALE_TO_CURRENCY[locale],
    maximumFractionDigits: fractionDigits,
    minimumFractionDigits: fractionDigits,
  });
}

export type DateInput = Date | string | number;

const DATE_ONLY_RE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Coerce a Date | epoch | string into a Date, or null if it isn't a real date.
 * Bare calendar strings ("2026-01-15") are read as local midnight so the day
 * never shifts across timezones (matching parseLocalDate in utils/date).
 */
function coerceDate(input: DateInput): Date | null {
  if (input instanceof Date) {
    return Number.isNaN(input.getTime()) ? null : input;
  }
  if (typeof input === "number") {
    const d = new Date(input);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const str = input.trim();
  if (!str) return null;
  if (DATE_ONLY_RE.test(str)) {
    const year = Number(str.slice(0, 4));
    const month = Number(str.slice(5, 7));
    const day = Number(str.slice(8, 10));
    return new Date(year, month - 1, day);
  }
  const parsed = new Date(str);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

const DEFAULT_DATE_OPTIONS: Intl.DateTimeFormatOptions = {
  year: "numeric",
  month: "short",
  day: "numeric",
};

/**
 * Locale-aware date formatter. Use this instead of `toLocaleDateString("en-US")`
 * or the English-only helpers so dates follow the app language (en → en-US,
 * fr → fr-FR). Non-date strings (e.g. freeform "Added today") are returned
 * unchanged rather than throwing or rendering "Invalid Date".
 */
export function formatDate(
  input: DateInput,
  locale: Locale,
  options: Intl.DateTimeFormatOptions = DEFAULT_DATE_OPTIONS,
): string {
  const date = coerceDate(input);
  if (!date) return typeof input === "string" ? input : "";
  return date.toLocaleDateString(LOCALE_TO_BCP47[locale], options);
}

/** Locale-aware time, e.g. "3:30 PM" / "15:30". */
export function formatTime(
  input: DateInput,
  locale: Locale,
  options: Intl.DateTimeFormatOptions = { hour: "numeric", minute: "2-digit" },
): string {
  const date = coerceDate(input);
  if (!date) return typeof input === "string" ? input : "";
  return date.toLocaleTimeString(LOCALE_TO_BCP47[locale], options);
}

/** Locale-aware date + time in one string. */
export function formatDateTime(
  input: DateInput,
  locale: Locale,
  options: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  },
): string {
  const date = coerceDate(input);
  if (!date) return typeof input === "string" ? input : "";
  return date.toLocaleString(LOCALE_TO_BCP47[locale], options);
}

/** Locale-aware "January 2026" / "janvier 2026". */
export function formatMonthYear(input: DateInput, locale: Locale): string {
  return formatDate(input, locale, { year: "numeric", month: "long" });
}

/** Locale-aware "Sunday, Jan 1" / "dimanche 1 janv.". */
export function formatWeekdayDate(input: DateInput, locale: Locale): string {
  return formatDate(input, locale, {
    weekday: "long",
    month: "short",
    day: "numeric",
  });
}

export function getCurrencySymbol(locale: Locale, currency?: string): string {
  return (
    getCurrencyParts(locale, currency).find((part) => part.type === "currency")
      ?.value ??
    currency ??
    LOCALE_TO_CURRENCY[locale]
  );
}

/**
 * Returns currency adornments in the order used by the locale.
 *
 * This is for editable numeric fields that render the number and currency as
 * separate elements. The optional currency code keeps placement correct when
 * a value is billed in a currency different from the locale default.
 */
export function getCurrencyAffixes(
  locale: Locale,
  currency?: string,
): CurrencyAffixes {
  const parts = getCurrencyParts(locale, currency);
  const currencyIndex = parts.findIndex((part) => part.type === "currency");
  const numberIndex = parts.findIndex(
    (part) =>
      part.type === "integer" ||
      part.type === "decimal" ||
      part.type === "fraction",
  );
  const symbol = getCurrencySymbol(locale, currency);
  const isPrefix = currencyIndex !== -1 && currencyIndex < numberIndex;

  return {
    prefix: isPrefix ? symbol : "",
    suffix: isPrefix ? "" : ` ${symbol}`,
  };
}

export function getCurrencyPrefix(locale: Locale): string {
  return locale === "fr" ? "~" : "~$";
}

export function getCurrencySuffix(locale: Locale, currency?: string): string {
  return getCurrencyAffixes(locale, currency).suffix;
}

/**
 * Format a plain "HH:MM[:SS]" time string according to the user's locale.
 * Use this for medication scheduled times and other places where the data
 * is a bare wall-clock time string (no date attached). For ISO datetimes
 * or Date objects, use `formatTime` instead.
 * - en → "9:30 AM"
 * - fr → "09:30"
 *
 * Returns the input unchanged when it can't be parsed.
 */
export function formatTimeHHMM(
  time: string | null | undefined,
  locale: Locale,
): string {
  if (!time) return "";
  const match = /^(\d{1,2}):(\d{2})/.exec(time);
  if (!match) return time;
  const h = Number(match[1]);
  const m = Number(match[2]);
  if (!Number.isFinite(h) || !Number.isFinite(m)) return time;
  const date = new Date(2000, 0, 1, h, m);
  return date.toLocaleTimeString(LOCALE_TO_BCP47[locale], {
    hour: "numeric",
    minute: "2-digit",
  });
}
